import { useState } from "react";
import { api } from "@/lib/api";
import { Ship, Anchor } from "lucide-react";
import { toast } from "sonner";

/**
 * Enkel onboarding — nye brukarar opprettar sin fyrste båt.
 * Invitasjons-flyten er fjerna: admin styrer alle medlemsskap manuelt.
 */
export default function OnboardingPage() {
  const [boatName, setBoatName] = useState("");
  const [shift, setShift] = useState("1");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!boatName.trim()) { toast.error("Skriv inn båtnavn"); return; }
    setLoading(true);
    try {
      await api.onboardUser({ boat_name: boatName.trim(), join_code: "", shift });
      toast.success("Velkommen om bord!");
      window.location.href = "/";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Noe gikk galt");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4" data-testid="onboarding-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="https://customer-assets.emergentagent.com/job_recipe-shop-plan/artifacts/n0xjz0mz_E3443421-DE5D-4719-8957-3EE6E82E98C2.png" alt="PantryBuddy" className="h-32 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-stone-900">Velkommen om bord</h1>
          <p className="text-sm text-stone-500 mt-2">Opprett din fyrste båt for å komme i gang</p>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-sailcloth-100 flex items-center justify-center flex-shrink-0">
              <Ship className="h-5 w-5 text-navy-800" />
            </div>
            <div>
              <div className="text-sm font-semibold text-stone-800">Ny båt</div>
              <div className="text-xs text-stone-400">Du blir eier og administrator</div>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-stone-500 mb-1 block">Båtnavn</label>
            <input
              value={boatName}
              onChange={(e) => setBoatName(e.target.value)}
              placeholder="F.eks. Frøy Spirit"
              className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none"
              data-testid="onboard-boat-name"
              autoFocus
            />
          </div>

          <div>
            <label className="text-xs font-medium text-stone-500 mb-2 block">Velg ditt skift</label>
            <div className="grid grid-cols-2 gap-3">
              {[{ v: "1", l: "Skift 1" }, { v: "2", l: "Skift 2" }].map((s) => (
                <button
                  key={s.v}
                  onClick={() => setShift(s.v)}
                  className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 text-sm font-semibold transition-all ${shift === s.v ? "border-seawater-600 bg-sailcloth-100 text-navy-800" : "border-stone-200 text-stone-500 hover:border-stone-300"}`}
                  data-testid={`onboard-shift-${s.v}`}
                >
                  <Anchor className="h-4 w-4" /> {s.l}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full rounded-lg bg-navy-800 px-4 py-3 text-sm font-medium text-white hover:bg-navy-900 transition-colors disabled:opacity-50"
            data-testid="onboard-submit"
          >
            {loading ? "Setter opp..." : "Kom i gang!"}
          </button>
        </div>
      </div>
    </div>
  );
}
